import CustomToast from "../../utils/functions/CustomToast";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import Errors from "../../utils/constants/Errors";

const request = async (operation, { setApiError, setIsLoading } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleCategoryRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

const normalizeQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;
  return normalizedParams;
};

const normalizeCategory = (category = {}) => ({
  ...category,
  id: category.id == null ? null : Number(category.id),
  sortOrder: Number(category.sortOrder ?? 0),
  active: category.active ?? true,
});

const normalizeCategoryListResponse = (data) => ({
  ...data,
  content: (data?.content ?? []).map(normalizeCategory),
});

const buildCategoryPayload = (category = {}) => ({
  id: category.id == null ? null : Number(category.id),
  name: category.name,
  description: category.description || "",
  sortOrder: Number(category.sortOrder ?? 0),
  active: Boolean(category.active ?? true),
});

export const getAllAddOnCategories = async (
  setCategoriesData,
  params,
  setIsLoading,
  setApiError,
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(ServerEndpoints.ADDON_CATEGORIES_ENDPOINT, {
        params: normalizeQueryParams(params),
      }),
    { setApiError, setIsLoading },
  );

  if (response.status === 200) {
    setCategoriesData(normalizeCategoryListResponse(response.data));
  }
};

export const createAddOnCategory = async (
  category,
  setState,
  setApiError,
  setIsLoading,
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().post(
        ServerEndpoints.ADDON_CATEGORIES_ENDPOINT,
        buildCategoryPayload(category),
      ),
    { setApiError, setIsLoading },
  );

  if (response.status === 201 || response.status === 200) {
    CustomToast("Category successfully created", "success");
    setState?.((prev) => !prev);
  }

  return normalizeCategory(response.data);
};

export const updateAddOnCategory = async (
  category,
  setState,
  setApiError,
  setIsLoading,
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().put(
        `${ServerEndpoints.ADDON_CATEGORIES_ENDPOINT}/${category.id}`,
        buildCategoryPayload(category),
      ),
    { setApiError, setIsLoading },
  );

  if (response.status === 200) {
    CustomToast("Category successfully updated", "success");
    setState?.((prev) => !prev);
  }

  return normalizeCategory(response.data);
};

export const deleteAddOnCategory = async (
  id,
  setState,
  setApiError,
  setIsLoading,
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${ServerEndpoints.ADDON_CATEGORIES_ENDPOINT}/${id}`),
    { setApiError, setIsLoading },
  );

  if (response.status === 204) {
    CustomToast("Category successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleCategoryRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || "The category could not be found.", "error");
      return;
    case 409:
      CustomToast(backendMessage || "The category cannot be completed because it is already in use.", "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR || Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
      return;
    default:
      if (err?.response == null) {
        CustomToast(Errors.API_ERROR, "error");
        return;
      }
      CustomToast(backendMessage || Errors.API_ERROR, "error");
  }
};
